import React, { useState } from "react";
import { ArrowLeft, Calendar, Clock, Share2, MessageCircle } from "lucide-react";
import { useParams } from "react-router-dom";
import { BLOG_POSTS, CONTACT } from "../data/mock";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ContactModal } from "../components/ContactModal";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [modalOpen, setModalOpen] = useState(false);

  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const related = BLOG_POSTS.filter((p) => p.id !== post?.id).slice(0, 3);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-3xl font-bold text-[#1a1a1a] mb-4">Article Not Found</h1>
          <a href="/blog" className="text-[#C9A84C] hover:underline">Back to Blog</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar onGetQuote={() => setModalOpen(true)} />
      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Hero image */}
      <div className="relative h-72 sm:h-96 mt-16">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-8 text-white">
            <a href="/blog" className="inline-flex items-center gap-2 text-[#C9A84C] text-sm mb-4 hover:gap-3 transition-all">
              <ArrowLeft size={14} /> All Articles
            </a>
            <span className="inline-block bg-[#C9A84C] text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">
              {post.category}
            </span>
            <h1 className="font-serif text-2xl sm:text-4xl font-bold leading-tight">{post.title}</h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-gray-300">
              <span className="flex items-center gap-1.5"><Calendar size={13} />{post.date}</span>
              <span className="flex items-center gap-1.5"><Clock size={13} />{post.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-600 text-lg leading-relaxed mb-6">{post.excerpt}</p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Hyderabad's interior design landscape has evolved dramatically over the past decade. 
            Homeowners in Gachibowli, Kondapur, Banjara Hills and HITEC City are increasingly 
            investing in professional interior design to transform their homes into beautiful, 
            functional spaces that reflect their personality and lifestyle.
          </p>

          <h2 className="font-serif text-2xl font-bold text-[#1a1a1a] mt-10 mb-4">Why Interior Design Matters</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            A well-designed home isn't just aesthetically pleasing — it improves your quality of life. 
            Good interior design maximizes the utility of every square foot, creates harmony between 
            spaces, and reflects your personality. In Hyderabad's urban homes, where every inch of 
            space is precious, professional interior design is more important than ever.
          </p>

          <h2 className="font-serif text-2xl font-bold text-[#1a1a1a] mt-10 mb-4">The Good Idea Interiors Approach</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            At Good Idea Interiors, we begin every project with a free consultation to understand 
            your vision, lifestyle, and budget. Our designers then create detailed 3D visualizations 
            so you can see exactly how your home will look before construction begins. This eliminates 
            surprises and ensures the final result perfectly matches your expectations.
          </p>

          <div className="bg-[#FAF8F5] rounded-xl p-6 my-8">
            <h3 className="font-serif text-xl font-bold text-[#1a1a1a] mb-3">Key Takeaways</h3>
            <ul className="space-y-2">
              {post.keywords.map((kw) => (
                <li key={kw} className="flex items-start gap-2 text-gray-600 text-sm">
                  <span className="text-[#C9A84C] mt-1">✓</span>
                  {kw}
                </li>
              ))}
            </ul>
          </div>

          <h2 className="font-serif text-2xl font-bold text-[#1a1a1a] mt-10 mb-4">Ready to Get Started?</h2>
          <p className="text-gray-600 leading-relaxed mb-6">
            Transforming your Hyderabad home starts with a conversation. Our design experts are 
            ready to understand your vision and create a personalized plan that fits your budget. 
            Book a free consultation today and take the first step toward your dream home.
          </p>
        </div>

        {/* CTA */}
        <div className="bg-[#1a1a1a] rounded-2xl p-8 text-center text-white mt-10">
          <h3 className="font-serif text-2xl font-bold mb-2">Inspired? Let's Build Your Dream Home</h3>
          <p className="text-gray-400 mb-6">Get a free consultation with our Hyderabad interior design experts</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setModalOpen(true)}
              className="bg-[#C9A84C] text-white font-bold px-8 py-3.5 rounded-full hover:bg-[#b8933e] transition-colors"
            >
              Get Free Quote
            </button>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(CONTACT.whatsappMsg)}`}
              target="_blank"
              rel="noreferrer"
              className="bg-[#25D366] text-white font-bold px-8 py-3.5 rounded-full hover:bg-[#1fb856] transition-colors flex items-center gap-2"
            >
              <MessageCircle size={16} />
              WhatsApp
            </a>
          </div>
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <div className="mt-16">
            <h3 className="font-serif text-2xl font-bold text-[#1a1a1a] mb-6">Related Articles</h3>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((rp) => (
                <div
                  key={rp.id}
                  className="bg-[#FAF8F5] rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => window.location.href = `/blog/${rp.slug}`}
                  data-testid={`related-post-${rp.id}`}
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={rp.image} alt={rp.title} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </div>
                  <div className="p-4">
                    <span className="text-[#C9A84C] text-xs font-semibold">{rp.category}</span>
                    <h4 className="font-serif text-sm font-bold text-[#1a1a1a] mt-1 line-clamp-2">{rp.title}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer onGetQuote={() => setModalOpen(true)} />
    </div>
  );
}
